/**
 * UiToast Composable
 * - 전역 토스트 메시지 관리
 */
import { ref } from 'vue';
import { appT } from '~/composables/useAppI18n';

interface ToastOptions {
  type: 'saving' | 'saved' | 'error' | 'success' | 'warning' | 'info';
  message: string;
  duration?: number;
}

const showSaveToast = ref(false);
const saveStatus = ref({
  type: 'saved' as ToastOptions['type'],
  message: '',
  class: ''
});

let toastTimeout: NodeJS.Timeout | null = null;

export const useUiToast = () => {
  /**
   * Toast 표시 함수
   */
  const showToast = (type: ToastOptions['type'], message: string, duration: number = 3000) => {
    // 기존 타이머 클리어
    if (toastTimeout) {
      clearTimeout(toastTimeout);
    }

    saveStatus.value = {
      type,
      message,
      class: `save-toast--${type}`
    };

    showSaveToast.value = true;

    // 자동 숨김 (저장 중이 아닐 때만)
    if (type !== 'saving' && duration > 0) {
      toastTimeout = setTimeout(() => {
        showSaveToast.value = false;
      }, duration);
    }
  };

  /**
   * Toast 숨김
   */
  const hideToast = () => {
    showSaveToast.value = false;
    if (toastTimeout) {
      clearTimeout(toastTimeout);
      toastTimeout = null;
    }
  };

  /**
   * 성공 토스트
   */
  const success = (message: string, duration?: number) => {
    showToast('saved', message, duration);
  };

  /**
   * 에러 토스트
   */
  const error = (message: string, duration?: number) => {
    showToast('error', message, duration);
  };

  /**
   * 저장 중 토스트
   */
  const saving = (message: string = appT('common.toast.saving')) => {
    showToast('saving', message, 0);
  };

  /**
   * 경고 토스트
   */
  const warning = (message: string, duration?: number) => {
    showToast('warning', message, duration);
  };

  /**
   * 정보 토스트
   */
  const info = (message: string, duration?: number) => {
    showToast('info', message, duration);
  };

  return {
    showSaveToast,
    saveStatus,
    showToast,
    hideToast,
    success,
    error,
    saving,
    warning,
    info
  };
};
